import React from 'react';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { toast } from 'sonner';

import { loadUserAgencies } from '../api/agency-service.js';
import { updatePaymentMethod } from '../utils/payment-utils.js';
import {
  loadFeatureFlags,
  getFeatureFlagObservable,
} from '../api/feature-flag-service.ts';
import { submitPayment } from '../api/signup-service.js';
import CustomButton from '../components/common/custom-button/custom-button.jsx';
import Text from '../components/common/custom-text/custom-text.jsx';
import { subscriptionTiers } from '../constants/subscription-tiers.js';
import useQuote from '../hooks/useQuote.js';
import { useCurrentUser } from '../store/current-user-context.jsx';
import {
  formatCurrency,
  roundCentsToNearestDollar,
} from '../utils/currency-utils.js';
import DowngradePlanDialog from './components/downgrade-plan-dialog/downgrade-plan-dialog.jsx';
import OrderSummary from './components/order-summary/order-summary.jsx';
import DiscountForm from './discount-form.jsx';
import PlanSelector from './plan-selector.jsx';

import '../styles/signup/signup.scss';

const PricingSubheader = React.memo(
  ({ plan, billingInterval, quote }) => {
    if (!plan || !billingInterval || !quote) {
      return null;
    }

    const { planPrice, freeWorkspaces, pricePerUnit = {} } = quote;
    const isFreelancer = plan.value === 'FREELANCER';
    const isAgencyOrTeam = plan.value === 'AGENCY' || plan.value === 'TEAM';
    const includedWorkspaces = isFreelancer ? 1 : freeWorkspaces;

    const tierPrice = isFreelancer
      ? (() => {
          const sortedPricePerUnitArray = Object.entries(pricePerUnit)
            .map(([priceStr, count]) => ({ price: Number(priceStr), count }))
            .sort((a, b) => b.price - a.price);
          return sortedPricePerUnitArray[0].price;
        })()
      : planPrice;

    if (isFreelancer) {
      return (
        <Text variant="b2" color="#2B4A78">
          {billingInterval === 'monthly'
            ? `Pricing starts at ${formatCurrency(
                tierPrice
              )} per month and includes ${includedWorkspaces} workspace. Our pricing is designed to reward your growth, with lower rates per workspace as you add more clients.`
            : `Pricing starts at ${formatCurrency(
                roundCentsToNearestDollar(tierPrice / 12)
              )} per month billed annually and includes ${includedWorkspaces} workspace. Our pricing is designed to reward your growth, with lower rates per workspace as you add more clients.`}
        </Text>
      );
    }

    if (isAgencyOrTeam) {
      return (
        <Text variant="b2" color="#2B4A78">
          {billingInterval === 'monthly'
            ? `Pricing starts at a flat rate of ${formatCurrency(
                tierPrice
              )} per month and includes ${includedWorkspaces} workspaces. Our pricing is designed to reward your growth, with lower rates per workspace as you add more clients.`
            : `Pricing starts at ${formatCurrency(
                roundCentsToNearestDollar(tierPrice / 12)
              )} per month billed annually and includes ${includedWorkspaces} workspaces. Our pricing is designed to reward your growth, with lower rates per workspace as you add more clients.`}
        </Text>
      );
    }

    return null;
  },
  (prevProps, nextProps) => {
    return prevProps.quote === nextProps.quote;
  }
);

const SignupReview = ({
  formData = {},
  isMobile = false,
  dispatch,
  trackEventOnce,
}) => {
  const { plan, discountCode, billingInterval, workspaceCount } = formData;
  const isAnnual = billingInterval === 'annual';
  const { price } = plan;
  const stripe = useStripe();
  const elements = useElements();
  const { user, logout } = useCurrentUser();
  const [cardError, setCardError] = React.useState(null);
  const [cardComplete, setCardComplete] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [agency, setAgency] = React.useState(null);
  const [isStripeUpgradeEnabled, setIsStripeUpgradeEnabled] =
    React.useState(false);
  const [downgradePlanDialogOpen, setDowngradePlanDialogOpen] =
    React.useState(false);
  const { stripeCustomerId } = agency || {};
  const { quote, loading } = useQuote({
    subscriptionTier: plan.value,
    annual: billingInterval === 'annual',
    quantity: workspaceCount,
    couponCode: discountCode,
    customerId: stripeCustomerId,
  });
  const PLAN_ORDER = ['FREELANCER', 'TEAM', 'AGENCY'];
  const isPlanDowngrade = agency?.subscriptionTier
    ? PLAN_ORDER.indexOf(plan.value) <
      PLAN_ORDER.indexOf(agency.subscriptionTier)
    : false;

  const handlePreviousStep = () => {
    dispatch({ type: 'UPDATE_ACTIVE_STEP', payload: 2 });
  };

  React.useEffect(() => {
    if (!user) return;

    loadUserAgencies()
      .then(agencies => {
        const currentAgency = agencies.find(
          agency => agency.id === user.agencyId
        );
        setAgency(currentAgency);
        loadFeatureFlags(currentAgency.id);
      })
      .catch(() => {
        setAgency(null);
      });
  }, [user]);

  React.useEffect(() => {
    const subscription = getFeatureFlagObservable(
      'STRIPE_UPGRADE_FEATURE_FLAG'
    ).subscribe(enabled => setIsStripeUpgradeEnabled(enabled));
    return () => subscription.unsubscribe();
  }, []);

  async function createStripeToken({ stripe, elements, elementType }) {
    if (!stripe || !elements) {
      throw new Error('Payment system not ready.');
    }

    const { token, error } = await stripe.createToken(
      elements.getElement(elementType)
    );

    if (error || !token) {
      throw new Error(error.message);
    }

    return token;
  }

  function handleSignupSuccess({ subscriptionTier, price, redirectUrl }) {
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'CompleteRegistration', {
        value: price,
        currency: 'USD',
      });
    }

    trackEventOnce('Registered', {
      event_category: 'Registration',
      event_label: subscriptionTier,
      value: price,
    });

    trackEventOnce('registration_step4', {
      agency_id: user.agencyId,
      plan: plan.name,
      term: billingInterval,
    });

    setTimeout(() => {
      window.location.href = redirectUrl;
    }, 500);
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    if (isPlanDowngrade) {
      setDowngradePlanDialogOpen(true);
      return;
    }

    handleConfirmedSubmit();
  }

  async function handleConfirmedSubmit() {
    setIsSubmitting(true);

    try {
      let paymentArgs = {
        subscriptionTier: plan.value,
        couponCode: discountCode,
        annual: isAnnual,
      };

      if (isStripeUpgradeEnabled) {
        await updatePaymentMethod({
          stripe,
          card: elements.getElement(CardElement),
          agencyId: agency.id,
        });
      } else {
        const token = await createStripeToken({
          stripe,
          elements,
          elementType: CardElement,
        });
        paymentArgs.paymentToken = token.id;
      }

      const response = await submitPayment(paymentArgs);
      const redirectUrl = response.data.redirectUrl;
      handleSignupSuccess({
        subscriptionTier: plan.value,
        price,
        redirectUrl,
      });
    } catch (err) {
      toast.error(
        err.message || 'An error occurred while processing your payment.'
      );
      setIsSubmitting(false);
    }
  }

  const cardStyle = {
    style: {
      base: {
        backgroundColor: '#fff',
        color: '#333',
        fontFamily: 'Hind, Avenir, Helvetica, arial',
        fontSmoothing: 'antialiased',
        fontSize: '16px',
        fontWeight: '400',
        lineHeight: '24px',
        letterSpacing: '0.16px',
        '::placeholder': {
          color: '#9CA3AF',
        },
      },
      invalid: {
        color: '#DC2626',
        iconColor: '#DC2626',
      },
    },
  };

  return (
    <div className="register-container">
      <div className="register-form">
        <section className="signup-form">
          <form onSubmit={handleSubmit}>
            <header className="review-form-header">
              <Text as="h4" variant="h4" color="#324975">
                Start your 14-day free trial!
              </Text>
              <PricingSubheader
                plan={plan}
                billingInterval={billingInterval}
                quote={quote}
              />
            </header>
            <PlanSelector formData={formData} dispatch={dispatch} />
            {isMobile && (
              <div
                className="mobile-order-summary"
                data-testid="mobile-order-summary"
              >
                <OrderSummary
                  quote={quote}
                  formData={formData}
                  loading={loading}
                />
              </div>
            )}
            <div className="checkout-container">
              <Text as="h5" variant="h5" color="#324975">
                Payment Information
              </Text>
              <div className="card-info-field">
                <Text
                  as="label"
                  variant="l2"
                  color="#324975"
                  htmlFor="card-element"
                >
                  Card Info
                </Text>
                <div className="payment-input-container">
                  <CardElement
                    options={cardStyle}
                    onChange={e => {
                      setCardError(e.error ? e.error.message : '');
                      setCardComplete(e.complete);
                    }}
                  />
                </div>
                {cardError && (
                  <Text variant="b2" color="#DC2626" role="alert">
                    {cardError}
                  </Text>
                )}
              </div>
              <DiscountForm />
            </div>
            <div className="signup-next-step-container">
              {isMobile ? (
                <CustomButton
                  variant="secondary"
                  onClick={logout}
                  data-testid="logout-button"
                >
                  Logout
                </CustomButton>
              ) : (
                <CustomButton
                  variant="secondary"
                  faIcon={faChevronLeft}
                  onClick={() => handlePreviousStep()}
                  data-testid={'signup-review-previous'}
                >
                  Back
                </CustomButton>
              )}
              <CustomButton
                type="submit"
                variant="primary"
                data-testid={'signup-review-next'}
                isLoading={isSubmitting}
                disabled={!agency || isSubmitting}
              >
                Access account
              </CustomButton>
            </div>
          </form>
        </section>
        <div className="testimonial-banner-container">
          <OrderSummary quote={quote} formData={formData} loading={loading} />
        </div>
      </div>
      <DowngradePlanDialog
        open={downgradePlanDialogOpen}
        setOpen={setDowngradePlanDialogOpen}
        to={plan.value}
        from={agency?.subscriptionTier}
        onContinue={() => {
          setDowngradePlanDialogOpen(false);
          handleConfirmedSubmit();
        }}
        onCancel={() => {
          dispatch({
            type: 'UPDATE_PLAN',
            payload: subscriptionTiers[agency.subscriptionTier] || subscriptionTiers.TEAM,
          });
          setDowngradePlanDialogOpen(false);
        }}
      />
    </div>
  );
};

export default SignupReview;
